/**
 * 부술 수 있는 오브젝트 카탈로그 (contracts/object-catalog.md).
 * 불변식: 6 ≤ fragmentCount ≤ 14, 임계값 순서
 * 펭귄 < 머그컵 < 폰 < 마우스 < 헤드폰 < 키보드 < 맥북 < 모니터.
 * 임계값은 "책상/바닥에 세게 내던지면 깨진다"는 손맛을 위해 보수적으로 낮게 잡음.
 * 크기: 히트 타깃 확보를 위해 실물 대비 약 1.7~2.4배로 **일관되게** 확대
 * (배율이 오브젝트마다 다르면 폰이 스티커처럼 보이는 등 스케일 비정합이 생긴다).
 * primitive 변경 시 models.ts 빌더의 scaleParts 배율도 함께 맞출 것.
 */
import type { BreakableObjectDef } from '../core/types';

export const OBJECT_CATALOG: BreakableObjectDef[] = [
  {
    id: 'penguin',
    label: '펭귄 피규어',
    breakThreshold: 4,
    fragmentCount: 6,
    baseScore: 80,
    mass: 0.25,
    color: 0xf5c531,
    primitive: { kind: 'cylinder', radius: 0.09, height: 0.22, radialSegments: 12 },
    sound: { plasticSample: true, layers: ['rubber'] }, // 밀도 있는 고무 피규어
  },
  {
    id: 'mug',
    label: '머그컵',
    breakThreshold: 5,
    fragmentCount: 7,
    baseScore: 100,
    mass: 0.4,
    color: 0x1b1c1f,
    primitive: { kind: 'cylinder', radius: 0.11, height: 0.24, radialSegments: 10 },
    sound: { layers: ['ceramic'] }, // 세라믹 파쇄(ceramic 레이어가 실측 샘플 내부 처리)
  },
  {
    id: 'phone',
    label: '스마트폰',
    breakThreshold: 5.5,
    fragmentCount: 8,
    baseScore: 150,
    mass: 0.2,
    color: 0x101216,
    primitive: { kind: 'box', size: [0.15, 0.03, 0.31] },
    sound: { layers: ['glass', ['metal', 0.35]] }, // 유리 파열 + 알루미늄 잔향 약간
  },
  {
    id: 'mouse',
    label: '마우스',
    breakThreshold: 6,
    fragmentCount: 6,
    baseScore: 120,
    mass: 0.3,
    color: 0x4dabf7,
    primitive: { kind: 'box', size: [0.14, 0.08, 0.22] },
    sound: { plasticSample: true, layers: ['plastic'] },
  },
  {
    id: 'headphones',
    label: '헤드폰',
    breakThreshold: 6.5,
    fragmentCount: 9,
    baseScore: 180,
    mass: 0.35,
    color: 0x17181c,
    primitive: { kind: 'box', size: [0.34, 0.37, 0.17] },
    sound: { plasticSample: true, layers: ['plastic', ['metal', 0.3]] }, // ABS + 금속 밴드 링 약간
  },
  {
    id: 'keyboard',
    label: '키보드',
    breakThreshold: 7.5,
    fragmentCount: 12,
    baseScore: 200,
    mass: 1.0,
    color: 0x343a40,
    primitive: { kind: 'box', size: [0.9, 0.1, 0.34] },
    sound: { plasticSample: true, layers: ['plastic'] },
  },
  {
    id: 'macbook',
    label: '맥북',
    breakThreshold: 8.5,
    fragmentCount: 12,
    baseScore: 280,
    mass: 1.6,
    color: 0xc2c6cd,
    primitive: { kind: 'box', size: [0.62, 0.036, 0.42] },
    // 실측 맥북 슬램(책상 강타) 어택 + CRT 스크린 파열 + 케이스 크랙.
    sound: { slamSample: true, layers: [['crt', 1.0], ['plastic', 0.45]] },
  },
  {
    id: 'monitor',
    label: '모니터',
    breakThreshold: 9.5,
    fragmentCount: 14,
    baseScore: 300,
    mass: 3.0,
    color: 0x212529,
    primitive: { kind: 'box', size: [1.1, 0.66, 0.12] },
    // 실측 슬램(책상 강타) 어택 + CRT 튜브 폭발 + 케이스 크랙.
    sound: { slamSample: true, layers: [['crt', 1.0], ['plastic', 0.45]] },
  },
];

const CATALOG_BY_ID = new Map(OBJECT_CATALOG.map((d) => [d.id, d]));

export function getObjectDef(id: string): BreakableObjectDef {
  const def = CATALOG_BY_ID.get(id);
  if (!def) throw new Error(`Unknown object def: ${id}`);
  return def;
}
